import { Chromosome, Particle, QoSProperty, QoS_WEIGHTS, QoS_MAXIMIZE } from '../types/algorithm';

/**
 * Gets quality value from matrix
 * matrix[task][service][property]
 */
function getQualityValue(
  matrix: number[][][],
  task: number,
  service: number,
  property: number
): number {
  if (matrix[task] && matrix[task][service] && matrix[task][service][property] !== undefined) {
    return matrix[task][service][property];
  }
  return 0;
}

/**
 * Normalizes a value to [0, 1] range
 * For maximize: normalizes to [0, 1] where 1 is best
 * For minimize: normalizes to [1, 0] where 0 is best (inverted)
 */
function normalizeValue(
  value: number,
  min: number,
  max: number,
  maximize: boolean
): number {
  if (max === min) return 0.5; // Avoid division by zero

  if (maximize) {
    return (value - min) / (max - min);
  } else {
    // For minimization, invert: best (min) becomes 1, worst (max) becomes 0
    return (max - value) / (max - min);
  }
}

/**
 * Calculates fitness for a chromosome/particle solution
 * Fitness formula: 0.1 * (Σ(weight[i] * normalized_quality[i])) / 9
 * 
 * IMPORTANT: Uses GLOBAL min/max from entire matrix for normalization
 * This ensures fitness values are comparable across different solutions
 */
export function calculateFitness(
  solution: number[], // Service selection array: solution[i] = service index for task i
  matrix: number[][][],
  taskCount: number,
  serviceCount: number
): number {
  // For Sequence workflow: aggregate properties across tasks
  const aggregatedValues: number[] = new Array(9).fill(0);

  // First pass: find GLOBAL min/max for normalization across ALL services
  // This ensures fitness values are comparable across different solutions
  const propertyRanges: Array<{ min: number; max: number }> = [];
  for (let prop = 0; prop < 9; prop++) {
    let min = Infinity;
    let max = -Infinity;

    // Find min/max across ALL services for ALL tasks (global normalization)
    for (let task = 0; task < taskCount; task++) {
      for (let service = 0; service < serviceCount; service++) {
        const value = getQualityValue(matrix, task, service, prop);
        if (value !== undefined && !isNaN(value)) {
          min = Math.min(min, value);
          max = Math.max(max, value);
        }
      }
    }

    // Fallback if no valid values found
    if (min === Infinity || max === -Infinity) {
      min = 0;
      max = 1;
    }
    
    propertyRanges.push({ min, max });
  }

  // Second pass: aggregate normalized values
  // For Sequence: sum for all properties
  for (let prop = 0; prop < 9; prop++) {
    let sum = 0;
    const { min, max } = propertyRanges[prop];
    const maximize = QoS_MAXIMIZE[prop];

    for (let task = 0; task < taskCount; task++) {
      const serviceIdx = solution[task];
      if (serviceIdx >= 0 && serviceIdx < serviceCount) {
        const value = getQualityValue(matrix, task, serviceIdx, prop);
        const normalized = normalizeValue(value, min, max, maximize);
        sum += normalized;
      }
    }

    aggregatedValues[prop] = sum;
  }

  // Calculate weighted sum
  let weightedSum = 0;
  for (let prop = 0; prop < 9; prop++) {
    weightedSum += QoS_WEIGHTS[prop] * aggregatedValues[prop];
  }

  // Final fitness: 0.1 * weightedSum / 9
  const fitness = 0.1 * (weightedSum / 9);
  return fitness;
}

/**
 * Calculates fitness for chromosome
 */
export function calculateChromosomeFitness(
  chromosome: Chromosome,
  matrix: number[][][],
  taskCount: number,
  serviceCount: number
): number {
  const fitness = calculateFitness(chromosome.x, matrix, taskCount, serviceCount);
  chromosome.fitness = fitness;
  return fitness;
}

/**
 * Calculates fitness for particle
 */
export function calculateParticleFitness(
  particle: Particle,
  matrix: number[][][],
  taskCount: number,
  serviceCount: number
): number {
  const fitness = calculateFitness(particle.position, matrix, taskCount, serviceCount);
  particle.fitness = fitness;
  
  // Update personal best
  if (fitness > particle.personalBestFitness) {
    particle.personalBest = [...particle.position];
    particle.personalBestFitness = fitness;
  }

  return fitness;
}

/**
 * Calculates aggregated response time (sum of response times)
 */
export function calculateResponseTime(
  solution: number[],
  matrix: number[][][],
  taskCount: number,
  serviceCount: number
): number {
  let total = 0;
  for (let task = 0; task < taskCount; task++) {
    const serviceIdx = solution[task];
    if (serviceIdx >= 0 && serviceIdx < serviceCount) {
      total += getQualityValue(matrix, task, serviceIdx, QoSProperty.ResponseTime);
    }
  }
  return total;
}

/**
 * Calculates energy consumption (scientifically correct)
 * Energy (J) = Power (W) × Time (s)
 * Formula: Energy = (alpha * RT + beta * Lat) / 1000 * P
 * where:
 *   - alpha=0.6, beta=0.4 (weighting factors)
 *   - RT, Lat are in milliseconds (from QWS dataset)
 *   - P=3.0W (average cloud server power consumption)
 *   - /1000 converts milliseconds to seconds for correct unit conversion
 * 
 * Scientific basis: Energy = Power × Time
 * Units: Joules = Watts × Seconds
 */
export function calculateEnergy(
  solution: number[],
  matrix: number[][][],
  taskCount: number,
  serviceCount: number
): number {
  const alpha = 0.6;  // Weight for response time
  const beta = 0.4;   // Weight for latency
  const P = 3.0;      // Power consumption in Watts (typical cloud server)

  let totalEnergy = 0;
  for (let task = 0; task < taskCount; task++) {
    const serviceIdx = solution[task];
    if (serviceIdx >= 0 && serviceIdx < serviceCount) {
      const rt = getQualityValue(matrix, task, serviceIdx, QoSProperty.ResponseTime); // ms
      const lat = getQualityValue(matrix, task, serviceIdx, QoSProperty.Latency);     // ms
      // Convert ms to seconds: divide by 1000
      // Energy (J) = Power (W) × Time (s)
      const energy = ((alpha * rt + beta * lat) / 1000) * P;
      totalEnergy += energy;
    }
  }
  return totalEnergy; // Returns energy in Joules (J)
}

/**
 * Calculates execution cost (improved model)
 * Cost Model: BaseCost + TimeBasedCost
 * Formula: Cost = (baseCostPerService × taskCount) + (ResponseTime × timeCostRate)
 * 
 * This model is more realistic than simple time-based cost:
 * - Base cost per service invocation (e.g., API call overhead, connection setup)
 * - Time-based cost (e.g., compute time, resource usage)
 * 
 * Parameters:
 *   - baseCostPerService: $0.001 per service invocation (typical cloud API cost)
 *   - timeCostRate: $0.00001 per millisecond (compute time cost)
 * 
 * Note: For relative comparison purposes, the exact values matter less than consistency.
 * This model provides more realistic cost estimation than pure time-based calculation.
 */
export function calculateCost(
  solution: number[],
  matrix: number[][][],
  taskCount: number,
  serviceCount: number
): number {
  // Cost model parameters (can be adjusted based on actual cloud pricing)
  const baseCostPerService = 0.001;  // Base cost per service invocation (in cost units)
  const timeCostRate = 0.00001;      // Cost per millisecond of execution time
  
  // Calculate total response time
  let totalResponseTime = 0;
  for (let task = 0; task < taskCount; task++) {
    const serviceIdx = solution[task];
    if (serviceIdx >= 0 && serviceIdx < serviceCount) {
      totalResponseTime += getQualityValue(matrix, task, serviceIdx, QoSProperty.ResponseTime);
    }
  }
  
  // Total cost = base cost (per service) + time-based cost
  const totalCost = (baseCostPerService * taskCount) + (totalResponseTime * timeCostRate);
  
  return totalCost;
}

